/* eslint-disable no-nested-ternary */
import { useContext, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Field, Form, Formik } from 'formik';

// ASSETS
import GeofencingIllustrationIcon from 'assets/GeofencingIllustrationIcon';

// COMPONENTS
import { InputContainer } from '../../FleetSettings.styles';
import { ToggleInput } from 'components/FormField';
import ErrorMessages from 'components/ErrorMessages';
import { SaveButton } from 'components/Buttons';
import GeofenceTable from './Table';
import GeofencingModal from './geofencingModal.component';
import { Spinner } from 'components/Loading';

// SELECTORS
import { selectFleetSettings } from 'services/FleetSettings/selectors';
import { selectErrors, selectGeofenceRules, selectLoading } from 'services/Geofences/selectors';

// RESOURCES
import { FleetSettings } from 'services/resources';

// STYLES
import colors from 'styles/colors';
import { SectionHeader, SectionContent, SectionHeaderTitle, SubSection, SectionHeaderActions } from 'styles/cards';
import {
  EmptyGeofencingContainer,
  GeofencingModalSection,
  GeofencingSection,
  MarginedDivider,
  SpinnerSubSection,
} from '../styles';
import { TableButtonWrapper } from 'styles/buttons';

// THUNKS
import { updateSettings, getFleetSettings } from 'services/FleetSettings/thunks';
import { listGeofenceRules } from 'services/Geofences/thunks';

// HOOKS
import { useAppDispatch } from 'hooks/store';

// CONSTANTS
import { FLASH_MESSAGE_TYPES } from 'constants/common';

// UTILS
import { intercom } from 'utils';
import { FlashMessageContext } from 'containers/FlashMessageProvider';
import { GeofencingIcon } from 'assets';
import { Label } from 'styles/form';

const GeofencingCard = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const fleetSettings = useSelector(selectFleetSettings());
  const loading = useSelector(selectLoading());
  const errors = useSelector(selectErrors());
  const { showFlashMessage } = useContext(FlashMessageContext);

  const geofences = useSelector(selectGeofenceRules());

  useEffect(() => {
    dispatch(getFleetSettings());
    dispatch(listGeofenceRules());
  }, [dispatch]);

  const handleSave = (values) => {
    return dispatch(
      updateSettings(fleetSettings.get('id'), {
        enableGeofencing: values.enableGeofencing,
      })
    )
      .then(() => {
        intercom.trackEvent('Updated geofencing settings', {});
        showFlashMessage(
          FLASH_MESSAGE_TYPES.success,
          intl.formatMessage({ id: 'views.FleetSettings.Geofencing.geofencingUpdated' })
        );
      })
      .catch(() => {
        showFlashMessage(
          FLASH_MESSAGE_TYPES.error,
          intl.formatMessage({ id: 'views.FleetSettings.Geofencing.geofencingCouldNotBeUpdated' })
        );
      });
  };

  const enableSave = (values: Partial<FleetSettings>, initialValues: Partial<FleetSettings>) => {
    return values.enableGeofencing === initialValues.enableGeofencing;
  };

  const initialValues = {
    enableGeofencing: fleetSettings.get('enableGeofencing'),
  };

  return (
    <GeofencingSection>
      <Formik initialValues={initialValues} onSubmit={handleSave}>
        {({ isSubmitting, values }) => (
          <Form>
            <SectionHeader>
              <SectionHeaderTitle>
                <GeofencingIcon color={colors.blue} />
                <FormattedMessage id="views.FleetSettings.Geofencing.title" />
              </SectionHeaderTitle>
              <SectionHeaderActions>
                <SaveButton
                  loading={loading || isSubmitting}
                  disabled={enableSave(values, initialValues)}
                  type="submit"
                />
              </SectionHeaderActions>
            </SectionHeader>
            <SectionContent>
              <div style={{ maxWidth: '600px' }}>
                <Label>
                  <FormattedMessage id="views.FleetSettings.Geofencing.enableGeofencingLabel" />
                </Label>
                <SubSection>
                  <FormattedMessage id="views.FleetSettings.Geofencing.enableGeofencingDescription" />
                </SubSection>
                <InputContainer>
                  <Field
                    as={ToggleInput}
                    type="checkbox"
                    name="enableGeofencing"
                    inlineLabel={intl.formatMessage({
                      id: 'views.FleetSettings.Geofencing.enableGeofencing',
                    })}
                    style={{ marginLeft: '10px' }}
                  />
                </InputContainer>
                <ErrorMessages errors={errors.get('enableGeofencing')} />
              </div>
              <MarginedDivider />
              {loading ? (
                <SpinnerSubSection>
                  <Spinner />
                </SpinnerSubSection>
              ) : geofences.size > 0 ? (
                <>
                  <Label>
                    <FormattedMessage id="views.FleetSettings.Geofencing.tableSectionLabel" />
                  </Label>
                  <SubSection>
                    <FormattedMessage id="views.FleetSettings.Geofencing.description" />
                  </SubSection>
                  <SubSection>
                    <GeofenceTable />
                  </SubSection>
                </>
              ) : (
                <EmptyGeofencingContainer>
                  <GeofencingIllustrationIcon color={colors.fleetBlue} />
                  <FormattedMessage id="views.FleetSettings.Geofencing.emptyDescription" />
                </EmptyGeofencingContainer>
              )}
              <ErrorMessages errors={errors.get('enableGeofencing')} />
            </SectionContent>
          </Form>
        )}
      </Formik>
      <GeofencingModalSection emptyState={geofences.size === 0}>
        <SubSection>
          <TableButtonWrapper>
            <GeofencingModal.GeofenceButton />
          </TableButtonWrapper>
        </SubSection>
      </GeofencingModalSection>
    </GeofencingSection>
  );
};

export default GeofencingCard;
